import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, MapPin, Check } from "lucide-react";
import { useBrand } from "@/context/BrandContext";
import Wordmark from "@/components/Wordmark";
import BrightLabsCredit from "@/components/BrightLabsCredit";
import api, { formatApiErrorDetail } from "@/lib/api";

const columns = [
  {
    title: "Explore",
    links: [
      { label: "Our Meals", to: "/meals" },
      { label: "Meal Plans", to: "/plans" },
      { label: "How It Works", to: "/how-it-works" },
      { label: "Our Food Standards", to: "/food-standards" },
      { label: "Delivery & Service Areas", to: "/delivery-areas" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Our Story", to: "/our-story" },
      { label: "Mothers Supporting Mothers", to: "/mothers-supporting-mothers" },
      { label: "FAQs", to: "/faqs" },
      { label: "Contact", to: "/contact" },
      { label: "Start Your Plan", to: "/get-started" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/legal/privacy" },
      { label: "Terms and Conditions", to: "/legal/terms" },
      { label: "Delivery Policy", to: "/legal/delivery" },
      { label: "Cancellation & Refunds", to: "/legal/refunds" },
      { label: "Allergy Disclaimer", to: "/legal/allergy" },
      { label: "Food Safety Information", to: "/legal/food-safety" },
    ],
  },
];

const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      await api.post("/leads/newsletter", { email });
      setState({ status: "done", message: "Thank you — we will keep you posted." });
      setEmail("");
    } catch (err) {
      setState({
        status: "error",
        message: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <form onSubmit={submit} className="mt-5 w-full max-w-sm" data-testid="newsletter-form">
      <label htmlFor="newsletter-email" className="text-xs font-semibold uppercase tracking-[0.16em] text-white">
        Get menu updates
      </label>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid="newsletter-email-input"
          className="min-h-[52px] flex-1 rounded-control border border-white/35 bg-white/15 px-5 text-sm font-semibold text-white placeholder:font-medium placeholder:text-white/70 focus:border-honey focus:outline-none"
        />
        <button
          type="submit"
          data-testid="newsletter-submit"
          disabled={state.status === "loading"}
          className="min-h-[52px] whitespace-nowrap rounded-control bg-amber px-6 text-sm font-extrabold text-white transition-colors hover:bg-honey disabled:opacity-60"
        >
          {state.status === "loading" ? "Sending…" : "Notify me"}
        </button>
      </div>
      {state.message && (
        <p
          data-testid="newsletter-message"
          className={`mt-2 flex items-center gap-1.5 text-xs ${
            state.status === "error" ? "text-honey" : "text-white"
          }`}
        >
          {state.status === "done" && <Check className="h-3.5 w-3.5" />}
          {state.message}
        </p>
      )}
    </form>
  );
};

export const Footer = () => {
  const brand = useBrand();
  return (
    <footer className="relative overflow-hidden bg-forest text-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(70% 60% at 88% 0%, rgba(251,215,154,0.14) 0%, rgba(251,215,154,0) 60%)",
        }}
      />
      <div className="mbm-container relative grid gap-12 py-16 lg:grid-cols-[1.3fr_repeat(3,1fr)]">
        <div>
          <Wordmark tone="cream" size="md" withDescriptor testId="footer-wordmark" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white">
            {brand.tagline}
          </p>
          <ul className="mt-6 space-y-2.5 text-sm text-white">
            <li className="flex items-center gap-2.5">
              <MapPin className="h-4 w-4 shrink-0 text-honey" aria-hidden="true" />
              {brand.contact.name} · {brand.contact.addressLine}
            </li>
            <li className="flex items-center gap-2.5">
              <Mail className="h-4 w-4 shrink-0 text-honey" aria-hidden="true" />
              <a href={`mailto:${brand.contact.email}`} data-testid="footer-email" className="inline-flex min-h-[44px] items-center transition-colors hover:text-honey">
                {brand.contact.email}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <Phone className="h-4 w-4 shrink-0 text-honey" aria-hidden="true" />
              <a href={`tel:${brand.contact.phone.replace(/\s/g, "")}`} data-testid="footer-phone" className="inline-flex min-h-[44px] items-center transition-colors hover:text-honey">
                {brand.contact.phone}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <MessageCircle className="h-4 w-4 shrink-0 text-honey" aria-hidden="true" />
              <a
                href={`https://wa.me/${brand.contact.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-whatsapp"
                className="inline-flex min-h-[44px] items-center transition-colors hover:text-honey"
              >
                WhatsApp us
              </a>
            </li>
          </ul>
          <NewsletterForm />
        </div>

        {columns.map((col) => (
          <div key={col.title}>
            <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-white">
              {col.title}
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace(/\//g, "-")}`}
                    className="inline-flex min-h-[44px] items-center text-white underline-offset-4 transition-colors hover:text-honey hover:underline"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="relative border-t border-white/25">
        <div className="mbm-container flex flex-col gap-4 py-6 text-xs text-white/95 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {brand.legalEntity}. {brand.contact.fssai}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <BrightLabsCredit />
            <span aria-hidden="true" className="hidden h-3 w-px bg-white/35 sm:block" />
            {brand.social.map((s) => (
              <a
                key={s.label}
                href={s.href}
                data-testid={`footer-social-${s.label.toLowerCase()}`}
                className="transition-colors hover:text-white"
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
